import ThirdsRepositoryImp from '../app/infrastructure/thirds/thirds-repository-implementation';
import { Third, CatalogItem, Municipality, ThirdFactory } from '../app/domain/models/thirds/third';
import { Filter, PaginationData } from '../domain/pagination/models';
import { cacheManager } from '../infrastructure/cache-manager';
import { getCookie } from '../utils/http-client';

// Helper to get auth context
const getAuthContext = () => {
    const uid = getCookie('uid') || localStorage.getItem('uid') || "";
    const cid = getCookie('cid') || localStorage.getItem('cid') || null;
    return { uid, cid };
};

const repo = new ThirdsRepositoryImp();
const CACHE_PREFIX = 'thirds';

async function getThirds(page = 1, limit = 10, search: Filter[] = []): Promise<PaginationData<Third[]> | undefined> {
    const normalizedFilters = [...search].sort((a, b) => a.label.localeCompare(b.label));
    const filtersKey = JSON.stringify(normalizedFilters);
    const cacheKey = `${CACHE_PREFIX}-list-${page}-${limit}-${filtersKey}`;

    try {
        const { uid, cid } = getAuthContext();
        const cached = cacheManager.getItem<any>(cacheKey);

        if (cached && search.length === 0) {
            return cached;
        }

        const type = search.find(f => f.label === 'type')?.value || 'all';
        const response: any = await repo.get(uid, cid, type, page, limit, search).toPromise();
        const rawData = response?.data || response;

        // Normalize backend "items" list to "data" to match frontend codebase contract
        if (rawData && Array.isArray(rawData.items)) {
            rawData.data = rawData.items;
            delete rawData.items;
        }

        let data = rawData;

        if (Array.isArray(rawData)) {
            data = rawData.map((item: any) => ThirdFactory.create(item));
        } else if (rawData && Array.isArray(rawData.data)) {
            data = {
                ...rawData,
                data: rawData.data.map((item: any) => ThirdFactory.create(item))
            };
        }

        if (data && search.length === 0) {
            cacheManager.setItem(cacheKey, data);
        }

        return data;
    } catch (error) {
        console.error("Error fetching thirds, using fallback", error);
        const cached = cacheManager.getItem<any>(cacheKey);
        if (cached) {
            return cached;
        }
        throw error;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function createThird(data: Third, companyId?: string | number): Promise<any> {
    try {
        const { uid, cid: defaultCid } = getAuthContext();
        const cid = companyId !== undefined && companyId !== null ? String(companyId) : defaultCid;
        const type = data.provider ? 'provider' : (data.client ? 'client' : 'other');
        const response = await repo.create(uid, cid, data, type).toPromise();
        cacheManager.invalidate(CACHE_PREFIX);
        const result = response?.data || response;
        return result ? ThirdFactory.create(result) : undefined;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function editThird(id: number, data: Third): Promise<any> {
    try {
        const { uid, cid } = getAuthContext();
        const response = await repo.edit(uid, cid, data, id).toPromise();
        cacheManager.invalidate(CACHE_PREFIX);
        const result = response?.data || response;
        return result ? ThirdFactory.create(result) : undefined;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

async function getThirdById(id: string | number): Promise<Third | undefined> {
    try {
        const { uid, cid } = getAuthContext();
        const cacheKey = `${CACHE_PREFIX}-detail-${id}`;
        const cached = cacheManager.getItem<Third>(cacheKey);
        if (cached) return cached;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await repo.getById(uid, cid, id.toString()).toPromise();
        const rawData = response?.data || response;
        const data = rawData ? ThirdFactory.create(rawData) : undefined;

        if (data) cacheManager.setItem(cacheKey, data);
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

async function getThirdByIdentification(identification: string): Promise<Third | undefined> {
    if (!identification) {
        throw new Error('Identification is required');
    }
    try {
        const { uid, cid } = getAuthContext();
        const cacheKey = `${CACHE_PREFIX}-identification-${identification}`;
        const cached = cacheManager.getItem<Third>(cacheKey);
        if (cached) return cached;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await repo.getByIdentification(uid, cid, identification).toPromise();

        // El backend devuelve una lista o un objeto paginado para consultas de búsqueda
        const rawThird = response?.data?.data ? response.data.data[0] : (response?.data || response);
        const third = rawThird ? ThirdFactory.create(rawThird) : undefined;
        
        if (third) cacheManager.setItem(cacheKey, third);
        return third;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

async function deleteThird(id: string | number): Promise<void> {
    try {
        const { uid, cid } = getAuthContext();
        await repo.delete(uid, cid, id.toString()).toPromise();
        cacheManager.invalidate(CACHE_PREFIX);
    } catch (error) {
        console.error(error);
        throw error;
    }
}

async function inactivateThird(id: string | number): Promise<void> {
    try {
        const { uid, cid } = getAuthContext();
        await repo.inactivate(uid, cid, id.toString()).toPromise();
        cacheManager.invalidate(CACHE_PREFIX);
    } catch (error) {
        console.error(error);
        throw error;
    }
}

async function reactivateThird(id: string | number): Promise<void> {
    try {
        const { uid, cid } = getAuthContext();
        await repo.reactivate(uid, cid, id.toString()).toPromise();
        cacheManager.invalidate(CACHE_PREFIX);
    } catch (error) {
        console.error(error);
        throw error;
    }
}

async function getIdentificationTypes(): Promise<any> {
    const { uid, cid } = getAuthContext();
    return await repo.getIdentificationTypes(uid, cid).toPromise().then(res => res || []);
}

async function getOrganizations(): Promise<CatalogItem[]> {
    const { uid, cid } = getAuthContext();
    return await repo.getOrganizations(uid, cid).toPromise().then(res => res || []);
}

async function getLiabilities(): Promise<CatalogItem[]> {
    const { uid, cid } = getAuthContext();
    return await repo.getLiabilities(uid, cid).toPromise().then(res => res || []);
}

async function getRegimes(): Promise<CatalogItem[]> {
    const { uid, cid } = getAuthContext();
    return await repo.getRegimes(uid, cid).toPromise().then(res => res || []);
}

async function getMunicipalities(query?: string): Promise<Municipality[]> {
    const { uid, cid } = getAuthContext();
    return await repo.getMunicipalities(uid, cid, query).toPromise().then(res => res || []);
}

export {
    getThirds,
    createThird,
    editThird,
    getThirdById,
    getThirdByIdentification,
    deleteThird,
    inactivateThird,
    reactivateThird,
    getIdentificationTypes,
    getOrganizations,
    getLiabilities,
    getRegimes,
    getMunicipalities
};
